import { forwardRef, useEffect, useMemo, useState } from "react";

import AutocompleteSelect from "@/common/components/Autocomplete/AutocompleteSelect";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useErrorToast } from "@/common/hooks/useErrorToast";
import {
  AutocompleteImplementationProps,
  AutocompleteValueIdProps,
} from "@/common/utils/autocomplete";
import { translateError } from "@/common/utils/errorHandling";
import { GithubRepositoryDisplay } from "@/integration/components/github/GithubRepositoryDisplay";
import { useGithubRepositories } from "@/integration/hooks/useGithubRepositories";
import { GithubRepo } from "@reflag/shared/githubConnectionAPI";

export type GithubRepositoryAutocompleteSelectProps =
  AutocompleteImplementationProps<GithubRepo> &
    AutocompleteValueIdProps<GithubRepo>;

export const GithubRepositoryAutocompleteSelect = forwardRef<
  HTMLButtonElement,
  GithubRepositoryAutocompleteSelectProps
>(function GithubRepositoryAutocompleteSelect(
  {
    valueAsId,
    value: valueProp,
    placeholder = "Select a GitHub repository",
    canClear,
    onChange,
    ...autocompleteProps
  }: GithubRepositoryAutocompleteSelectProps,
  ref,
) {
  const { org } = useEnvironment();
  const [searchValue, setSearchValue] = useState("");
  const errorToast = useErrorToast();
  // Treat fullName as the id for the autocomplete
  const valueId = useMemo(
    () => (valueAsId ? valueProp : valueProp?.fullName),
    [valueAsId, valueProp],
  );

  const {
    data: repos = [],
    isLoading,
    isFetching,
    isError,
    error,
  } = useGithubRepositories();

  const internalValue = useMemo(() => {
    return repos.find(({ fullName }) => fullName === valueId);
  }, [valueId, repos]);

  const suggestions = useMemo(() => {
    const filteredRepos = repos.filter((repo) =>
      repo.fullName.toLowerCase().includes(searchValue.toLowerCase()),
    );
    if (internalValue) {
      return [
        internalValue,
        ...filteredRepos.filter(
          (repo) => repo.fullName !== internalValue.fullName,
        ),
      ];
    }
    return filteredRepos;
  }, [repos, searchValue, internalValue]);

  useEffect(() => {
    if (isError) {
      const { title, description } = translateError(
        error,
        `Failed to search for GitHub repositories`,
      );

      errorToast({
        title,
        description,
        duration: 5000,
      });
    }
  }, [isError, error, errorToast]);

  return (
    <AutocompleteSelect
      ref={ref}
      canClear={canClear}
      isDisabled={isError || !org.integrations.github}
      isLoading={isLoading}
      isLoadingSuggestions={isFetching}
      itemKeyProperty="id"
      itemRenderFn={(repo, search) => (
        <GithubRepositoryDisplay
          py={0.5}
          repository={repo}
          search={search}
          showIcon={false}
        />
      )}
      loadingText="Loading..."
      placeholder={placeholder}
      suggestions={suggestions}
      value={internalValue}
      onChange={(newValue: GithubRepo | null) => {
        setSearchValue("");
        if (!newValue) {
          if (canClear) onChange?.(null);
          return;
        }

        // If valueAsId is true, return only the fullName of the selected repository
        if (valueAsId) {
          onChange?.(newValue?.fullName ?? null);
        } else {
          onChange?.(newValue ?? null);
        }
      }}
      onClear={() => {
        setSearchValue("");
        if (canClear) onChange?.(null);
      }}
      onSearchInput={setSearchValue}
      {...autocompleteProps}
    />
  );
});
